'use client';

import { useSafeNavigation } from '@/hooks/useSafeNavigation';
import { useThemeColor } from '@/hooks/useThemeColor';
import { MagazineSeries } from '@/types/magazines';
import MagazineSeriesCategoryUnit from '@/(routers)/magazines/list/components/unit/MagazineSeriesCategoryUnit';

type MagazineSeriesListProps = {
    seriesList: MagazineSeries[];
};

export default function MagazineSeriesList({ seriesList }: MagazineSeriesListProps) {
    const { safePush } = useSafeNavigation();
    const { scheme } = useThemeColor();

    const getSeriesDetailPath = (series: MagazineSeries) => {
        const encodedHubSeriesCd = encodeURIComponent(series.hub_series_cd);

        if (series.hub_type === 'stage') {
            return `/magazines/dive/stage/series/${encodedHubSeriesCd}`;
        }

        return `/magazines/dive/series/${encodedHubSeriesCd}`;
    };

    return (
        <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-8 md:gap-4">
            {seriesList.map((series) => {
                const seriesNumber = String(series.hub_series_cd).padStart(2, '0');
                const magazineCount = series.magazines.length;

                return (
                    <div
                        key={`${series.hub_type}-${series.hub_series_cd}`}
                        onClick={() => safePush(getSeriesDetailPath(series))}
                        className="
                            rounded-none md:rounded-xl
                            bg-color-white-base
                            hover:bg-[#E6F7F8]
                            transition-colors
                            duration-200
                            md:shadow-full
                            px-5
                            py-5
                            cursor-pointer
                        "
                    >
                        <div className="mb-2">
                            <div className="flex items-center justify-between gap-3">
                                <p className="font-corp font-bold text-xs tracking-wide text-color-co-gray-dark">
                                    SERIES {seriesNumber}
                                </p>

                                <div
                                    className={`shrink-0 inline-flex items-center rounded-full ${scheme.bg} px-2.5 py-0.5`}
                                >
                                    <span className="font-corp font-bold text-xs tracking-wider text-color-white-base">
                                        {magazineCount}冊連動
                                    </span>
                                </div>
                            </div>

                            {series.categories.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                                    {series.categories.map((category) => (
                                        <MagazineSeriesCategoryUnit
                                            key={category.magazine_series_category_cd}
                                            label={category.name}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <h3 className="min-h-[2.6em] font-corp font-bold text-lg md:text-xl tracking-widest text-color-base-layout-logo leading-tight mb-3">
                            {series.name}
                        </h3>

                        <div className="flex justify-start gap-2">
                            {series.magazines.map((magazine) => (
                                <img
                                    key={magazine.magazine_cd}
                                    src={magazine.eyecatch_public_url ?? '/images/magazine/eyecatch_default.png'}
                                    alt={magazine.title}
                                    className="w-14 md:w-16 aspect-[600/734] rounded-md object-cover shadow-sm"
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
